import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FONT_BODY } from './marketingStyles';

export function PrimaryButton({
  to,
  children,
}: {
  to: string;
  children: ReactNode;
}) {
  return (
    <Link
      to={to}
      className={`${FONT_BODY} flex h-12 shrink-0 items-center justify-center rounded-lg bg-forest-800 px-6 text-[15px] font-semibold whitespace-nowrap text-white transition-colors hover:bg-forest-950`}
    >
      {children}
    </Link>
  );
}

// Mint: the student entry (easy for a child to spot, but not competing
// with the adult sign-up as a second solid-green button), and the call to
// action on the dark manifesto band.
export function MintButton({
  to,
  children,
}: {
  to: string;
  children: ReactNode;
}) {
  return (
    <Link
      to={to}
      className={`${FONT_BODY} flex h-12 shrink-0 items-center justify-center rounded-lg border border-forest-600/25 bg-mint px-6 text-[15px] font-semibold whitespace-nowrap text-forest-950 transition-colors hover:bg-[#bdf4d6]`}
    >
      {children}
    </Link>
  );
}

export function OutlineButton({
  to,
  children,
}: {
  to: string;
  children: ReactNode;
}) {
  return (
    <Link
      to={to}
      className={`${FONT_BODY} flex h-12 shrink-0 items-center justify-center rounded-lg border-[1.5px] border-forest-800 bg-white px-6 text-[15px] font-semibold whitespace-nowrap text-forest-800 transition-colors hover:bg-sage`}
    >
      {children}
    </Link>
  );
}

// A link to a section of the landing page: on the landing page itself it
// scrolls there smoothly; from any other page (the footer pages) it goes to
// "/#<section>", and the landing page scrolls to it on arrival.
export function SectionLink({
  targetId,
  children,
  className = 'text-[14px] text-ink-soft',
  onNavigate,
}: {
  targetId: string;
  children: ReactNode;
  className?: string;
  onNavigate?: () => void;
}) {
  const onLanding = useLocation().pathname === '/';
  if (!onLanding) {
    return (
      <Link
        to={`/#${targetId}`}
        onClick={onNavigate}
        className={`${FONT_BODY} inline-flex items-center text-left font-medium whitespace-nowrap ${className}`}
      >
        {children}
      </Link>
    );
  }
  return (
    <button
      type="button"
      onClick={() => {
        onNavigate?.();
        document
          .getElementById(targetId)
          ?.scrollIntoView({ behavior: 'smooth' });
      }}
      className={`${FONT_BODY} text-left font-medium whitespace-nowrap ${className}`}
    >
      {children}
    </button>
  );
}
